import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { forgotPassword } from '../api/auth-api';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      // Backend returns 200 regardless of whether the email exists. We rely on
      // the uniform response and show the same confirmation to the user, so no
      // branching is needed here.
      await forgotPassword(email.trim());
    } catch {
      // Swallow network errors so we don't accidentally reveal user existence.
    } finally {
      setSubmitted(true);
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-success-50 dark:bg-success-500/20 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-10 h-10 text-success-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">Check your email</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            If an account matches <strong className="text-gray-900 dark:text-gray-100">{email}</strong>, we've sent a password reset link. It expires in 30 minutes.
          </p>
          <Link to="/login" className="inline-block text-sm text-primary-600 dark:text-primary-400 hover:underline">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-8">
        <h2 className="text-3xl font-bold text-center mb-2 text-gray-900 dark:text-gray-100">Forgot password?</h2>
        <p className="text-center text-gray-600 dark:text-gray-400 mb-8">
          Enter the email you use to sign in. We'll send a reset link if it matches an account.
        </p>

        <form noValidate onSubmit={handleSubmit} className="space-y-5">
          <Input
            label="Email address"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoFocus
          />

          <Button type="submit" className="w-full py-3 text-base" isLoading={submitting}>
            Send reset link
          </Button>
        </form>

        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
          Remembered it?{' '}
          <Link to="/login" className="text-primary-600 dark:text-primary-400 hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
